import {BaseElement, MsgLabel, OSkinableInit} from "back/commons/basis";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {IReg, REG} from "lib/commons/registry";
import {JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IDepotUiEnv} from "lib/store/depot";
import {resPath} from "lib/store/res";
import {OUtBrowserInit, UtBrowser} from "back/store/views/utBrowser";
import {JNodeShortProps, URLTREE} from "lib/store/urlTree";
import {Action} from "lib/commons/actions";
import {ICloseable} from "back/commons/widgets/popupable";

/**
 * Dialogue de sélection d'un dossier ou d'une ressource via l'UrlTree Browser.
 */
export interface UtSelector extends BaseElement {
	initialize(init: OUtSelectorInit): this
}

export interface OUtSelectorInit extends OSkinableInit {

	/** Path de la res ou du dossier pré-sélectionné. */
	initialPath?: resPath

	/** Accepte la sélection (true) ou la refuse avec un msg pour le user (string). */
	checkSel?: (utSelector: UtSelector) => true | string

	/** Contexte d'appel auprès duquel retourner le résultat de la sélection. */
	holder?: ICloseable<JNodeShortProps | null>


	selBtnLabel?: string

	utBrowser?: OUtBrowserInit
}

export class UtSelector extends BaseElement {

	reg: IReg<IDepotUiEnv>;

	checkSel?: (utSelector: UtSelector) => true | string

	doBtn: Button;
	msg: MsgLabel;

	currentSel: JNodeShortProps;

	get holder(): ICloseable<JNodeShortProps | null> {return this._holder || POPUP.findPopupableParent(this)}

	_holder?: ICloseable<JNodeShortProps | null>

	protected _initialize(init: OUtSelectorInit) {
		this.reg = this.findReg(init);
		this.checkSel = init.checkSel;
		this._holder = init.holder;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("standard-dialog", sr);
		this._initAndInstallSkin(this.localName, init);
		const utInit = Object.assign<OUtBrowserInit, OUtBrowserInit>({
			reg: this.reg,
			onSelChange: this.onSelChange,
			defaultAction: new Action().setExecute(this.validAndDo.bind(this)),
			nodeFilter: (n) => !URLTREE.isQueryPath(n.n)
		}, init.utBrowser);
		const utBrowser = sr.appendChild(<UtBrowser î={utInit}/>) as UtBrowser;
		const footer = sr.appendChild(<div id="footer"/>);
		this.msg = footer.appendChild(<c-msg id="msg"/>) as MsgLabel;
		this.doBtn = footer.appendChild(<Button id="do" class="default" disabled ui-context="dialog" label={init.selBtnLabel || "Sélectionner"} onclick={this.onDoBtn}/>) as Button;
		footer.appendChild(<Button id="cancel" ui-context="dialog" label="Annuler" onclick={this.onCancelBtn}/>);
		utBrowser.initializedAsync.then(() => {
			if (init.initialPath != null) utBrowser.selectRes(init.initialPath);
		});
		this.checkSelValid();
	}

	protected onSelChange(this: void, utBrowser: UtBrowser, sel: JNodeShortProps[]) {
		const me = DOMSH.findHost<UtSelector>(utBrowser);
		me.currentSel = sel && sel.length > 0 ? sel[0] : null;
		me.checkSelValid();
	}

	checkSelValid(): boolean {
		let valid: true | string = true;
		if (this.currentSel == null) {
			valid = "Sélectionnez un contenu";
		} else if (this.checkSel) {
			valid = this.checkSel(this);
		}
		this.doBtn.disabled = valid !== true;
		this.msg.setCustomMsg(valid !== true ? valid : null);
		return valid === true;
	}

	validAndDo() {
		if (this.checkSelValid()) this.holder.close(this.currentSel);
	}

	cancel() {
		this.holder?.close();
	}

	onCancelBtn(this: Button) {
		(DOMSH.findHost(this) as UtSelector).cancel();
	}

	onDoBtn(this: Button) {
		(DOMSH.findHost(this) as UtSelector).validAndDo();
	}
}

REG.reg.registerSkin('store-ut-selector', 1, /* language=CSS */ `

	c-button {
		min-width: 6em;
	}

	#msg {
		flex: 1;
		margin: 0 .5em;
		justify-content: start;
	}
`);

customElements.define("store-ut-selector", UtSelector);
